import React from "react";
import { motion } from "framer-motion";

/*
  Each live GoWave product gets its own full-width section, themed in its
  own brand colours. `theme: "dark"` flips the section to a dark canvas,
  `flip: true` puts the visual on the left.
*/
const products = [
  {
    id: "classscribe",
    eyebrow: "EdTech · B2C & B2B",
    name: "ClassScribe",
    tagline: "Your AI-Powered Learning Companion",
    desc:
      "Chat directly with your recordings and documents, generate mocktests in seconds, and follow an adaptive curriculum that learns how you learn. Built for students, coaching institutes, and schools.",
    features: ["Chat-with-Notes AI", "AI-Generated Mocktests", "Live Sessions", "Peer-Based Learning"],
    stats: [
      { num: "AI", label: "Built-in" },
      { num: "7-Day", label: "Free Trial" },
      { num: "Web", label: "+ PWA" },
    ],
    url: "https://www.classscribe.in",
    cta: "Visit classscribe.in",
    color: "#2f7df6",
    color2: "#fbbf24",
    theme: "light",
    flip: false,
    mock: "study",
  },
  {
    id: "classscribe-lms",
    eyebrow: "EdTech · For Institutions",
    name: "ClassScribe LMS",
    tagline: "A Complete LMS for Schools & Institutes",
    desc:
      "Run your entire institution from one dashboard — manage batches, attendance, assignments and auto-graded assessments, then surface real-time performance analytics for every learner.",
    features: ["Batch Management", "Auto-Grading", "Attendance & Reports", "Institute Analytics"],
    stats: [
      { num: "Multi", label: "Batch" },
      { num: "Auto", label: "Grading" },
      { num: "Live", label: "Analytics" },
    ],
    url: "https://lms.classscribe.in",
    cta: "Visit lms.classscribe.in",
    color: "#6366f1",
    color2: "#22d3ee",
    theme: "light",
    flip: true,
    mock: "dashboard",
  },
  {
    id: "hms",
    eyebrow: "Healthcare · Enterprise",
    name: "HMS",
    tagline: "End-to-End Hospital Management",
    desc:
      "Digitise the entire patient journey — records, smart appointment scheduling, billing, and pharmacy — with an AI assistant that supports diagnostics and keeps your hospital running around the clock.",
    features: ["Patient Records", "AI Diagnostics", "Smart Billing", "Pharmacy Mgmt"],
    stats: [
      { num: "24/7", label: "Monitoring" },
      { num: "AI", label: "Diagnostics" },
      { num: "100%", label: "Paperless" },
    ],
    url: "https://hms-gowave.vercel.app",
    cta: "View HMS",
    color: "#0ea5a4",
    color2: "#34d399",
    theme: "light",
    flip: false,
    mock: "health",
  },
  {
    id: "likar",
    eyebrow: "Retail ERP · India",
    name: "LiKAR",
    tagline: "Smart Liquor Shop Management",
    desc:
      "Complete inventory, sales, purchase and excise-compliance system with an AI billing assistant — built specifically for Indian liquor retailers. Maharashtra & Assam ready, all states supported.",
    features: ["AI Billing Assistant", "Excise Compliance", "Inventory & POS", "Sales Intelligence"],
    stats: [
      { num: "20+", label: "States" },
      { num: "100%", label: "Excise Ready" },
      { num: "AI", label: "Billing" },
    ],
    url: "https://likar.app",
    cta: "Visit likar.app",
    color: "#34d399",
    color2: "#a3e635",
    theme: "dark",
    flip: true,
    mock: "pos",
  },
];

// Build hex+alpha tint CSS variables (avoids color-mix for older build tooling)
const ALPHA = { "08": "14", "10": "1a", "14": "24", "18": "2e", "22": "38", "30": "4d", "35": "59", "40": "66" };
function themeVars(color, color2) {
  const vars = { "--c": color, "--c2": color2 };
  Object.keys(ALPHA).forEach((k) => {
    vars[`--c-${k}`] = `${color}${ALPHA[k]}`;
  });
  return vars;
}

function Mock({ type, color, color2 }) {
  return (
    <div className="ps-mock" style={themeVars(color, color2)}>
      <div className="ps-mock-glow" />
      <div className="ps-mock-window">
        <div className="ps-mock-bar">
          <span className="ps-dot" />
          <span className="ps-dot" />
          <span className="ps-dot" />
          <span className="ps-mock-pill">{type === "pos" ? "POS" : type === "health" ? "HMS" : "AI"}</span>
        </div>
        <div className="ps-mock-body">
          <div className="ps-mock-side">
            <span className="ps-mock-sq" />
            <span className="ps-mock-ln" />
            <span className="ps-mock-ln short" />
            <span className="ps-mock-ln" />
            <span className="ps-mock-ln short" />
          </div>
          <div className="ps-mock-main">
            <div className="ps-mock-row">
              <span className="ps-mock-stat" />
              <span className="ps-mock-stat" />
              <span className="ps-mock-stat" />
            </div>
            <div className="ps-mock-chart">
              <span style={{ height: "42%" }} />
              <span style={{ height: "70%" }} />
              <span style={{ height: "55%" }} />
              <span style={{ height: "88%" }} />
              <span style={{ height: "64%" }} />
              <span style={{ height: "92%" }} />
            </div>
            <div className="ps-mock-ln wide" />
            <div className="ps-mock-ln wide short" />
          </div>
        </div>
      </div>
      <div className="ps-float ps-float-1">{type === "pos" ? "₹" : type === "health" ? "+" : "AI"}</div>
      <div className="ps-float ps-float-2" />
    </div>
  );
}

function ProductShowcaseComponent() {
  return (
    <div className="product-showcase" id="products">
      <motion.div
        className="ps-intro text-center container"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="section-badge section-badge-light">Our Products</div>
        <h2>
          Software That's <span className="accent-grad">Already Live</span>
        </h2>
        <p className="ps-intro-sub">
          Every product ships with artificial intelligence built in. These are
          the platforms our customers use today — each one a complete, focused
          product in its own right.
        </p>
      </motion.div>

      {products.map((p) => (
        <section
          key={p.id}
          className={`ps-section ${p.theme === "dark" ? "ps-dark" : "ps-light"} ${p.flip ? "ps-flip" : ""}`}
          style={themeVars(p.color, p.color2)}
        >
          <div className="container">
            <div className="ps-grid">
              <motion.div
                className="ps-copy"
                initial={{ opacity: 0, x: p.flip ? 40 : -40 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <span className="ps-eyebrow">{p.eyebrow}</span>
                <h3 className="ps-name">{p.name}</h3>
                <p className="ps-tagline">{p.tagline}</p>
                <p className="ps-desc">{p.desc}</p>

                <div className="ps-features">
                  {p.features.map((f) => (
                    <span className="ps-chip" key={f}>
                      <span className="ps-chip-dot" />
                      {f}
                    </span>
                  ))}
                </div>

                <div className="ps-stats">
                  {p.stats.map((s) => (
                    <div className="ps-stat" key={s.label}>
                      <span className="ps-stat-num">{s.num}</span>
                      <span className="ps-stat-label">{s.label}</span>
                    </div>
                  ))}
                </div>

                <div className="ps-cta-row">
                  <a className="ps-btn-primary" href={p.url} target="_blank" rel="noopener noreferrer">
                    {p.cta} <span aria-hidden>→</span>
                  </a>
                  <a className="ps-btn-ghost" href="mailto:contact@gowave.in?subject=Demo%20Request">
                    Request a Demo
                  </a>
                </div>
              </motion.div>

              <motion.div
                className="ps-visual"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.7, delay: 0.15 }}
              >
                <Mock type={p.mock} color={p.color} color2={p.color2} />
              </motion.div>
            </div>
          </div>
        </section>
      ))}

      <div className="ps-more">
        <div className="container text-center">
          <h4>Need something built just for you?</h4>
          <p>
            ERP, CRM, field-force apps or a bespoke AI workflow — if you can
            describe it, we can ship it.
          </p>
          <a className="ps-btn-primary ps-more-btn" href="mailto:contact@gowave.in?subject=Custom%20Software%20Inquiry">
            Talk to us about custom software <span aria-hidden>→</span>
          </a>
        </div>
      </div>
    </div>
  );
}

export default ProductShowcaseComponent;
